import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { geoRestrictionService, GeoRestriction } from '../services/geoRestrictionService';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const GeoRestrictions: React.FC = () => {
  const [restrictions, setRestrictions] = useState<GeoRestriction[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRestriction, setEditingRestriction] = useState<GeoRestriction | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadRestrictions();
  }, []);

  const loadRestrictions = async () => {
    try {
      setLoading(true);
      const response = await geoRestrictionService.getAllRestrictions();
      setRestrictions(response.data || []);
    } catch (error: any) {
      message.error('Failed to load geo-restrictions: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRestriction(null);
    form.resetFields();
    form.setFieldsValue({
      restrictionType: 'block',
      isActive: true,
      restrictedFeatures: ['all'],
    });
    setModalVisible(true);
  };

  const handleEdit = (restriction: GeoRestriction) => {
    setEditingRestriction(restriction);
    form.setFieldsValue({
      countryCode: restriction.countryCode,
      countryName: restriction.countryName,
      region: restriction.region || '',
      restrictionType: restriction.restrictionType,
      reason: restriction.reason || '',
      isActive: restriction.isActive,
      restrictedFeatures: restriction.restrictedFeatures,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await geoRestrictionService.deleteRestriction(id);
      message.success('Geo-restriction deleted successfully');
      loadRestrictions();
    } catch (error: any) {
      message.error('Failed to delete geo-restriction: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingRestriction) {
        await geoRestrictionService.updateRestriction(editingRestriction._id, values);
        message.success('Geo-restriction updated successfully');
      } else {
        await geoRestrictionService.createRestriction(values);
        message.success('Geo-restriction created successfully');
      }
      
      setModalVisible(false);
      form.resetFields();
      setEditingRestriction(null);
      loadRestrictions();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error('Failed to save geo-restriction: ' + (error.message || 'Unknown error'));
    }
  };

  const columns = [
    {
      title: 'Country',
      key: 'country',
      render: (_: any, record: GeoRestriction) => (
        <Space>
          <Tag color="blue">{record.countryCode}</Tag>
          <span>{record.countryName}</span>
          {record.region && <Tag>{record.region}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'restrictionType',
      key: 'restrictionType',
      render: (type: string) => (
        <Tag color={type === 'block' ? 'red' : 'green'}>
          {type === 'block' ? <StopOutlined /> : <CheckCircleOutlined />} {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Features',
      dataIndex: 'restrictedFeatures',
      key: 'restrictedFeatures',
      render: (features: string[]) => (
        <Space>
          {features.map((f) => (
            <Tag key={f}>{f}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: GeoRestriction) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this geo-restriction?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>Geo-Restrictions</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadRestrictions}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Add Restriction
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={restrictions}
          rowKey="_id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingRestriction ? 'Edit Geo-Restriction' : 'Add Geo-Restriction'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingRestriction(null);
        }}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="countryCode"
            label="Country Code (ISO 3166-1 alpha-2)"
            rules={[{ required: true, message: 'Please enter country code' }]}
            tooltip="e.g., US, IN, GB"
          >
            <Input placeholder="e.g., US" maxLength={2} style={{ textTransform: 'uppercase' }} />
          </Form.Item>

          <Form.Item
            name="countryName"
            label="Country Name"
            rules={[{ required: true, message: 'Please enter country name' }]}
          >
            <Input placeholder="e.g., United States" />
          </Form.Item>

          <Form.Item
            name="region"
            label="Region (Optional)"
            tooltip="Optional region/state within the country"
          >
            <Input placeholder="e.g., California" />
          </Form.Item>

          <Form.Item
            name="restrictionType"
            label="Restriction Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="block">Block (Block this country)</Option>
              <Option value="allow">Allow (Only allow this country - whitelist mode)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="restrictedFeatures"
            label="Restricted Features"
            rules={[{ required: true }]}
            tooltip="Select which features to restrict"
          >
            <Select mode="multiple">
              <Option value="registration">Registration</Option>
              <Option value="marketplace">Marketplace</Option>
              <Option value="posting">Posting</Option>
              <Option value="all">All Features</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason (Optional)"
            tooltip="Reason for this restriction (shown to users)"
          >
            <TextArea rows={3} placeholder="e.g., Service not available in this region" />
          </Form.Item>

          <Form.Item
            name="isActive"
            valuePropName="checked"
            label="Active"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GeoRestrictions;

